import { useState } from 'react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { TextField } from '@scripsteam/scrips-signal-ds';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/auth/auth-store';
import {
  useCreateLabReport,
  useLabRequestReports,
} from '@/scheduling/services/use-lab-request-reports';
import { postResultReviewRecorded } from '@/scheduling/services/l360-result-review-event';
import { useUpdateLabOrderStatus } from '@/scheduling/services/use-update-lab-order-status';
import { LAB_ORDER_STATUS } from '@/types/scheduling-api';
import {
  CALCULATED_LDL,
  loincForTestCode,
  needsCalculatedLdl,
  rangeError,
} from '@/scheduling/lib/l360-lab-loinc';
import type { CreateLabReportObservation, LabReportDto } from '@/types/lab-results-api';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  labRequestId: string;
  patientName: string;
  /** The row's lab request status. The save moves Sample collected to Result received. */
  requestStatus?: string | null;
}

const CALC_LDL_KEY = 'calculated-ldl';

function parseValue(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** L5: the lab desk enters discrete results, saves them, then records the review. */
export function EnterResultsDialog({ open, onOpenChange, patientId, labRequestId, patientName, requestStatus }: Props) {
  const userId = useAuthStore((s) => s.user?.userId);
  const { data, isLoading, isError } = useLabRequestReports(patientId, labRequestId, open);
  const createReport = useCreateLabReport(patientId, labRequestId);
  const updateStatus = useUpdateLabOrderStatus();
  const qc = useQueryClient();
  const [values, setValues] = useState<Record<string, string>>({});
  const [saveError, setSaveError] = useState<string | null>(null);

  const tests = (data?.tests ?? []).filter(
    (t) => !t.isOmitted && t.status?.toLowerCase() !== 'cancelled',
  );
  const existing = latestReport(data?.reports);

  const filled: CreateLabReportObservation[] = [];
  const mapped = tests.filter((t) => loincForTestCode(t.testCode));
  let allValid = mapped.length > 0;
  for (const t of mapped) {
    const map = loincForTestCode(t.testCode)!;
    const v = parseValue(values[t.id]);
    if (v === null || rangeError(map, v)) {
      allValid = false;
      continue;
    }
    filled.push({
      patientLabRequestTestId: t.id,
      loinc: map.loinc,
      display: map.display,
      valueNumeric: v,
      unit: map.unit,
      status: 'final',
    });
  }
  const calcLdl = needsCalculatedLdl(tests.map((t) => t.testCode));
  // Hint only at TG 400 or more: the lab may report LDL by another method, so the field stays editable.
  const tgTest = mapped.find((t) => t.testCode === '84478');
  const tgValue = tgTest ? parseValue(values[tgTest.id]) : null;
  const ldlNotCalculated = tgValue !== null && tgValue >= 400;
  const calcValue = parseValue(values[CALC_LDL_KEY]);
  const calcError = calcValue !== null ? rangeError(CALCULATED_LDL, calcValue) : null;
  if (calcLdl) {
    // Optional: empty is fine and is left out of the report. A typed value must be in range.
    if (calcError) {
      allValid = false;
    } else if (calcValue !== null) {
      // No test row, and no addedByLab: the lab does not add a billable test.
      filled.push({
        patientLabRequestTestId: null,
        loinc: CALCULATED_LDL.loinc,
        display: CALCULATED_LDL.display,
        valueNumeric: calcValue,
        unit: CALCULATED_LDL.unit,
        status: 'final',
      });
    }
  }
  const canSave = allValid && !!userId;

  const handleSave = async () => {
    if (!canSave || !userId) return;
    setSaveError(null);
    let result;
    try {
      result = await createReport.mutateAsync({
        issuedAt: new Date().toISOString(),
        status: 'final',
        source: 'Transcribed',
        observations: filled,
      });
    } catch {
      setSaveError("Results couldn't be saved. Check the values and try again.");
      toast.error("Results couldn't be saved");
      return;
    }
    toast.success('Results saved');
    onOpenChange(false);
    // The event never blocks or changes the save outcome, and never waits on the status update.
    postResultReviewRecorded({
      patientId,
      labRequestId,
      report: result.report,
      reviewerId: userId!,
    });
    // The report save does not change the request status. A failure here keeps the saved results.
    if (requestStatus === LAB_ORDER_STATUS.SampleCollected) {
      try {
        await updateStatus.mutateAsync({ id: labRequestId, status: LAB_ORDER_STATUS.ResultReceived });
      } catch {
        toast.warning('Results saved; status not updated');
        void qc.invalidateQueries({ queryKey: ['lab-orders'] });
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Enter results</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-text-muted">{patientName}</p>
        {isLoading ? (
          <p className="text-sm">Loading tests...</p>
        ) : isError || !data ? (
          <p role="alert" className="text-sm text-status-error">
            Tests could not be loaded.
          </p>
        ) : !data.storeAvailable ? (
          <p className="text-sm">Results can&apos;t be saved on this environment</p>
        ) : existing ? (
          <ExistingReport report={existing} />
        ) : (
          <div className="flex flex-col gap-3">
            {tests.length > 0 && !tests.some((t) => loincForTestCode(t.testCode)) ? (
              <p className="text-sm">
                None of these tests can be entered here yet (no LOINC mapping).
              </p>
            ) : null}
            {tests.map((t) => {
              const map = loincForTestCode(t.testCode);
              const name = t.testDisplay ?? t.testCode ?? 'Test';
              if (!map) {
                return (
                  <div key={t.id} className="text-sm">
                    <div>{name}</div>
                    <div className="text-xs text-text-muted">
                      No LOINC mapping, not entered here
                    </div>
                  </div>
                );
              }
              const v = parseValue(values[t.id]);
              const fieldError = v !== null ? rangeError(map, v) : null;
              return (
                <div key={t.id}>
                  <TextField
                    label={`${name} (${map.unit})`}
                    type="number"
                    inputMode="decimal"
                    value={values[t.id] ?? ''}
                    onChange={(e) => setValues((p) => ({ ...p, [t.id]: e.target.value }))}
                    error={fieldError ?? undefined}
                  />
                  <div className="text-xs text-text-muted">
                    LOINC {map.loinc} · {map.min} to {map.max} {map.unit}
                  </div>
                </div>
              );
            })}
            {calcLdl ? (
              <div>
                <TextField
                  label={`${CALCULATED_LDL.display} (${CALCULATED_LDL.unit})`}
                  type="number"
                  inputMode="decimal"
                  value={values[CALC_LDL_KEY] ?? ''}
                  onChange={(e) => setValues((p) => ({ ...p, [CALC_LDL_KEY]: e.target.value }))}
                  error={calcError ?? undefined}
                />
                {ldlNotCalculated ? (
                  <div className="text-xs text-text-muted">
                    Labs usually don't calculate LDL when triglycerides ≥ 400 mg/dL; check the report
                  </div>
                ) : null}
                <div className="text-xs text-text-muted">
                  Calculated by the lab (not an ordered test) · Optional · LOINC {CALCULATED_LDL.loinc} ·{' '}
                  {CALCULATED_LDL.min} to {CALCULATED_LDL.max} {CALCULATED_LDL.unit}
                </div>
              </div>
            ) : null}
            {!userId ? (
              <p className="text-xs text-text-muted">Sign in again to save results.</p>
            ) : null}
            {saveError ? (
              <p role="alert" className="text-sm text-status-error">
                {saveError}
              </p>
            ) : null}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {data?.storeAvailable && !existing ? (
            <Button
              onClick={() => void handleSave()}
              disabled={!canSave || createReport.isPending}
            >
              Verify and save
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The current report is the one with the highest version. */
function latestReport(reports: LabReportDto[] | undefined): LabReportDto | undefined {
  return reports?.reduce<LabReportDto | undefined>(
    (best, r) => (!best || r.version > best.version ? r : best),
    undefined,
  );
}

function ExistingReport({ report }: { report: LabReportDto }) {
  return (
    <div className="flex flex-col gap-2 text-sm">
      <p>Results saved (v{report.version})</p>
      <ul>
        {report.observations.map((o) => (
          <li key={o.id}>
            {o.display ?? o.loinc}: {o.valueNumeric} {o.unit}
          </li>
        ))}
      </ul>
    </div>
  );
}
